import { v4 as UUIDv4 } from 'uuid';
import { notifyError, notifyWarn } from '@shakkerai/pro-components';
import { requestShakeImage } from '@shakkerai/services';
import { translate } from '@shakkerai/util-i18n';
import { getImageOssKey, getImageOssPath, compressLocalImage, getRemoteImageSize } from '@shakkerai/util-image';
import { getLoadings } from '@shakkerai/pro-store';
import { controlNetList, MAX_GENERATOR_SIZE } from '@shakkerai/pro-constants';
import { MODEL_FLUX, MODEL_SDXL } from '@shakkerai/pro-models';
import { noPreprocessControlNetTypes, controlNetInitialValues } from '@shakkerai/pro-constants';
import { controlNetConfig, controlnetMap, processorSubParams } from '@shakkerai/pro-constants';
import { compressWithSuffix, copyOSSImage, uploadImage } from '@shakkerai/pro-utils';
import { fetchPrompt, findBestSizeByReferenceSize } from '@shakkerai/pro-utils';
import { getActiveGeneratorNodeId, getUnit, getUnitData, updateUnitData } from '@starflow/app/helpers/flow';
import { setTempState, getTempState, getProjectState } from '@starflow/app/store';
import { getSharedData, setSharedData } from '@starflow/app/store';
import { tryExecute } from '@starflow/app/utils/base';
import { mc } from '@shakkerai/report';

const stripToken = (url) => url.replace(/\?Token=[^&]+&?/i, '');

export const updateControlNet = (unitId, id, controlNetData, options) => {
  unitId = unitId || getActiveGeneratorNodeId();

  updateUnitData(
    unitId,
    (unitData) => ({
      ...unitData,
      controlnets: unitData.controlnets.map((item) => {
        return item.id !== id ? item : Object.assign({}, item, tryExecute(controlNetData, item));
      }),
    }),
    options,
  );
};

export const appendControlNet = (unitId, controlNetData, options) => {
  unitId = unitId || getActiveGeneratorNodeId();

  updateUnitData(
    unitId,
    (unitData) => ({
      ...unitData,
      controlnets: unitData.controlnets.concat(controlNetData),
    }),
    options,
  );
};

export const applyReferenceImage = async (element, unitId) => {
  unitId = unitId || getActiveGeneratorNodeId();

  const unit = getUnit(unitId);

  if (unit.type !== 'generator') {
    // 只能发送到生成器类型的节点
    return false;
  }

  const { controlnets, baseType } = unit.data;
  const isFluxModel = baseType === MODEL_FLUX;

  let controlWeight = 0.5;

  if (typeof element.controlWeight === 'number') {
    controlWeight = element.controlWeight;
  }

  const controlNetId = UUIDv4();

  if (isFluxModel && controlnets.length === 1) {
    updateUnitData(unitId, {
      controlnets: [
        {
          id: controlNetId,
          type: null,
          controlWeight: controlWeight,
          controlMode: 0,
          startingControlStep: 0,
          endingControlStep: 1,
          sourceImage: {
            id: element.id,
            url: element.url,
            width: element.width,
            height: element.height,
            innerTaskId: element.innerTaskId,
          },
        },
      ],
    });
  } else {
    if (controlnets.length >= 4) {
      notifyError(translate('imgref_limit', { value: 4 }));
      return;
    }

    appendControlNet(unitId, {
      id: controlNetId,
      type: null,
      controlWeight: controlWeight,
      controlMode: 0,
      startingControlStep: 0,
      endingControlStep: 1,
      sourceImage: {
        id: element.id,
        url: element.url,
        width: element.width,
        height: element.height,
        innerTaskId: element.innerTaskId,
      },
    });

    // updateControlNetImage(unitId, controlNetId, {
    //   id: element.id,
    //   url: element.url,
    //   width: element.width,
    //   height: element.height,
    //   controlWeight: controlWeight,
    // });
  }

  if (element.innerTaskId) {
    // 如果是生成的图，则需要增加一个前置生成器节点连接到图生图
    if (unit.flowId) {
      // 发送到流里的节点的情况
    } else {
      // 发送到独立生图单元的情况
    }
  }

  setTimeout(() => {
    setTempState({ exclusiveWindow: `controlnet-options-${controlNetId}` });
  }, 50);
};

export const applyControlNetFromImage = async (element, unitId, type) => {
  unitId = unitId || getActiveGeneratorNodeId();

  const { controlnets } = getUnitData(unitId);

  let controlNetId = null;
  let controlWeight = 0.5;

  if (typeof element.controlWeight === 'number') {
    controlWeight = element.controlWeight;
  }

  const existedCNItem = controlnets.find((item) => item.type === type);

  if (existedCNItem) {
    controlNetId = existedCNItem.id;

    const updatedData = {
      id: element.id,
      url: element.url,
      width: element.width,
      height: element.height,
      innerTaskId: element.innerTaskId,
    };

    if (type === 'face') {
      updateControlNetImage(unitId, controlNetId, updatedData, 'referenceImage', { referenceType: 'custom' });
    }

    updateControlNetImage(unitId, controlNetId, updatedData);
  } else {
    if (controlnets.length >= 4) {
      notifyError(translate('imgref_limit', { value: 4 }));
      return;
    }

    controlNetId = UUIDv4();

    const controlNetData = {
      id: controlNetId,
      type: type,
      controlWeight: controlWeight,
      controlMode: 0,
      resizeMode: 1,
      startingControlStep: 0,
      endingControlStep: 1,
      sourceImage: {
        id: element.id,
        url: element.url,
        width: element.width,
        height: element.height,
        innerTaskId: element.innerTaskId,
      },
    };

    if (type === 'face') {
      controlNetData.referenceType = 'custom';
      controlNetData.referenceImage = {
        id: element.id,
        url: element.url,
        width: element.width,
        height: element.height,
        innerTaskId: element.innerTaskId,
      };
    }

    appendControlNet(unitId, controlNetData);
  }

  setTimeout(() => {
    setTempState({ exclusiveWindow: `controlnet-options-${controlNetId}` });
  }, 50);
};

export const applyImg2ImgSourceImage = async (sourceImage, unitId) => {
  unitId = unitId || getActiveGeneratorNodeId();
  const { imageReference, controlnets } = getUnitData(unitId);

  if (!controlnets) return;

  const updatedParams = {
    imageReference: Object.assign({}, imageReference || {}, {
      id: UUIDv4(),
      width: sourceImage.width,
      mode: 'creative',
      label: translate('imgref_similarity_creative'),
      strength: sourceImage.strength || 0.25,
      height: sourceImage.height,
      url: sourceImage.url,
      innerTaskId: sourceImage.innerTaskId,
    }),
  };

  const faceControlNet = controlnets.find((item) => item.type === 'face');

  if (faceControlNet) {
    updatedParams.controlnets = controlnets.map((item) => {
      if (item.type === 'face') {
        return {
          ...item,
          referenceImage: {
            id: sourceImage.id,
            width: sourceImage.width,
            height: sourceImage.height,
            url: sourceImage.url,
          },
        };
      }

      return item;
    });
  }

  updateUnitData(unitId, updatedParams);
  setTimeout(() => {
    setTempState('exclusiveWindow', `image-input-options-${unitId}`);
  }, 10);
};

export const updateControlNetImage = async (unitId, controlNetId, image, imageFieldName = 'sourceImage', extraData) => {
  unitId = unitId || getActiveGeneratorNodeId();

  const { startLoading, stopLoading } = getLoadings();
  const { controlnets, baseType = MODEL_SDXL } = getUnitData(unitId);
  const controlNet = controlnets.find((item) => item.id === controlNetId);

  if (!controlNet) return;

  let { id, url, width, height, innerTaskId } = image || {};
  id = id || UUIDv4();

  // const { updateLoading, stopLoading } = getLoadings();

  if (image) {
    if (!width || !height) {
      const remoteSize = await getRemoteImageSize(url);
      width = remoteSize.width;
      height = remoteSize.height;
    }

    const fixedSize = findBestSizeByReferenceSize(width, height, MAX_GENERATOR_SIZE);
    const needPreprocess = controlNet.type && !noPreprocessControlNetTypes.includes(controlNet.type);

    if (needPreprocess) {
      // 没有图标的话，就去获取
      startLoading(`${unitId}-controlnet-${controlNetId}-processing`);

      copyOSSImage(url, true, '3')
        .then((res) => res.url)
        .then(getImageOssPath)
        .then(async (sourceImagePath) => {
          const previewImage = await fetchControlNetImage(sourceImagePath, baseType, controlNet, unitId);

          if (previewImage) {
            updateControlNet(unitId, controlNetId, { previewImage });
            stopLoading(`${unitId}-controlnet-${controlNetId}-processing`);
          }
        })
        .catch(() => {
          notifyError(translate('controlnet_fail_toast_title'), translate('controlnet_fail_toast_content'));
          stopLoading(`${unitId}-controlnet-${controlNetId}-processing`);
        });
    }

    let updatedWeight = controlNet.controlWeight;

    if (typeof image.controlWeight === 'number') {
      updatedWeight = image.controlWeight;
    }

    const updateData = {
      [imageFieldName]: { id, url, innerTaskId, width: fixedSize.width, height: fixedSize.height },
      controlWeight: updatedWeight,
      ...extraData,
    };

    if (!needPreprocess) {
      updateData.previewImage = url;
    }

    updateControlNet(unitId, controlNetId, updateData);
    return needPreprocess;
  }
};

export const uploadControlNetImage = async (unitId, controlNetId, file, imageFieldName = 'sourceImage') => {
  const { startLoading, stopLoading } = getLoadings();

  unitId = unitId || getActiveGeneratorNodeId();

  const loadingKey = `${unitId}-controlnet-${imageFieldName}-${controlNetId}-uploading`;

  try {
    startLoading(loadingKey);
    const { file: compressedFile, width, height, compressed } = await compressLocalImage(file, 2048, 2048);

    if (compressed) {
      notifyWarn(translate('img_resize_toast'));
    }

    const uploadResult = await uploadImage(compressedFile, false, 'img_reference').catch(() => ({ url: null }));

    if (!uploadResult || !uploadResult.url) {
      // notifyError(translate('upload_error'), translate('upload_fail_toast'));
      stopLoading(loadingKey);
      return;
    }

    const { url } = uploadResult;
    const sourceImage = { id: UUIDv4(), url, width, height };

    await updateControlNetImage(unitId, controlNetId, sourceImage, imageFieldName);

    stopLoading(loadingKey);

    return sourceImage;
  } catch {
    stopLoading(loadingKey);
  }
};

export const changeControlNetType = async (unitId, controlNetId, type) => {
  unitId = unitId || getActiveGeneratorNodeId();

  const { startLoading, stopLoading } = getLoadings();
  const { controlnets, baseType = MODEL_SDXL } = getUnitData(unitId);

  if (!controlnets) return;

  const controlNet = controlnets.find((item) => item.id === controlNetId);

  if (!controlNet) return;

  const sourceImageUrl = controlNet.sourceImage?.url;
  const nextData = { previewImage: null, type, id: controlNetId, ...controlNetInitialValues };

  nextData.controlWeight = 1;
  nextData.preprocessor = controlNetConfig[type]?.[baseType]?.preprocessors[0];
  nextData.model = controlNetConfig[type]?.[baseType]?.models[0];

  if (sourceImageUrl) {
    if (noPreprocessControlNetTypes.includes(type)) {
      nextData.previewImage = sourceImageUrl;
      stopLoading(`${unitId}-controlnet-${controlNetId}-processing`);
    } else {
      // 没有图标的话，就去获取
      startLoading(`${unitId}-controlnet-${controlNetId}-processing`);

      copyOSSImage(sourceImageUrl, true, '3')
        .then((res) => res.url)
        .then(getImageOssPath)
        .then(async (sourceImagePath) => {
          const previewImage = await fetchControlNetImage(sourceImagePath, baseType, nextData, unitId);

          if (previewImage) {
            updateControlNet(unitId, controlNetId, { previewImage });
            stopLoading(`${unitId}-controlnet-${controlNetId}-processing`);
          }
        })
        .catch(() => {
          notifyError(translate('controlnet_fail_toast_title'), translate('controlnet_fail_toast_content'));
          stopLoading(`${unitId}-controlnet-${controlNetId}-processing`);
        });
    }
  }

  updateControlNet(unitId, controlNetId, nextData);

  // 临上线前加的需求，急着上，先在这里这样写，后面有需要再抽离
  setTimeout(() => {
    const { baseType } = getUnitData(unitId);

    if (baseType === MODEL_FLUX) {
      const updateData = {};
      const fluxCNs = controlNetList.filter((item) => item.baseTypes.includes(MODEL_FLUX)).map((item) => item.value);

      if (fluxCNs.includes(type)) {
        updateData.controlWeight = 0.7;
      }

      if (type === 'entity_control') {
        updateData.controlWeight = 0.75;
      }

      if (type === 'ip_adapter') {
        updateData.controlWeight = 0.8;
        updateData.endingControlStep = 0.6;
      }

      if (type === 'depth' || type === 'canny' || type === 'openpose') {
        updateData.controlWeight = 0.7;
        updateData.endingControlStep = 0.3;
      }

      if (type === 'openpose') {
        updateData.resizeMode = '1';
      }

      if (updateData.controlWeight || updateData.endingControlStep) {
        updateControlNet(unitId, controlNetId, updateData);
      }
    }
  }, 50);
};

export const appendEmptyControlNet = async (unitId) => {
  unitId = unitId || getActiveGeneratorNodeId();

  const { elements, nodeMap, currentNodeId } = getProjectState();
  const unit = nodeMap[unitId || currentNodeId];

  if (!unit) return;

  const controlNetId = UUIDv4();

  if (unit.type === 'inpaint') {
    const inpaintElement = elements.find((item) => item.inpaint?.unitId === unitId);

    appendControlNet(unitId, {
      id: controlNetId,
      type: 'depth',
      controlWeight: 0.75,
      controlMode: 0,
      resizeMode: 1,
      startingControlStep: 0,
      endingControlStep: 1,
      sourceImage: inpaintElement,
      previewImage: inpaintElement?.url,
    });
  } else {
    let inheritedSourceImage = null;
    const { controlnets, imageReference } = unit.data.data;

    if (imageReference) {
      inheritedSourceImage = JSON.parse(JSON.stringify(imageReference));
    } else if (controlnets.length) {
      const prevItemSourceImage = controlnets[controlnets.length - 1].sourceImage;
      inheritedSourceImage = prevItemSourceImage ? JSON.parse(JSON.stringify(prevItemSourceImage)) : null;
    }

    appendControlNet(unitId, {
      id: controlNetId,
      type: null,
      sourceImage: inheritedSourceImage || null,
      previewImage: inheritedSourceImage?.url || null,
      controlWeight: 1,
      controlMode: 0,
      resizeMode: 1,
      startingControlStep: 0,
      endingControlStep: 1,
    });
  }

  return controlNetId;
};

export const tryFixControlNetsForBaseType = async (unitId) => {
  unitId = unitId || getActiveGeneratorNodeId();

  const { controlnets, baseType = MODEL_SDXL, presetBaseModelId } = getUnitData(unitId);

  if (!controlnets) return;

  let needFix = false;

  const isStarFlow = presetBaseModelId === 'base_checkpoint';

  const fixedControlNets = controlnets
    .map((item) => {
      if (!item.type) {
        return item;
      }

      if (item.type && typeof item.type !== 'string') {
        needFix = true;
        return null;
      }

      if (controlnetMap[item.type]?.baseTypes?.includes?.(baseType)) {
        // 限定starflow基础模型的cn，切换checkpoint之后清除
        if (controlnetMap[item.type]?.starFlowOnly && !isStarFlow) {
          needFix = true;
          return {
            ...item,
            type: null,
          };
        }
        return item;
      }

      needFix = true;

      return {
        ...item,
        type: null,
      };
    })
    .filter(Boolean);

  if (needFix) {
    updateUnitData(unitId, { controlnets: fixedControlNets }, { skipTracking: true });
  }
};

export const removeControlNet = (unitId, controlNetId) => {
  updateUnitData(unitId, (unitData) => ({
    ...unitData,
    controlnets: unitData.controlnets.filter((item) => item.id !== controlNetId),
  }));
};

export const clearReferenceImage = () => {};

export const refreshControlNets = async () => {
  // @deprecated as no need to refresh.
};

export const refetchControlNetImage = async (unitId, controlNetId) => {
  const { controlnets, baseType = MODEL_SDXL } = getUnitData(unitId);

  if (!controlnets?.length) return;

  const controlNet = controlnets.find((item) => item.id === controlNetId);

  if (!controlNet) return;

  const sourceImageUrl = controlNet.sourceImage?.url;

  if (!sourceImageUrl) return;

  if (noPreprocessControlNetTypes.includes(controlNet.type)) {
    updateControlNet(unitId, controlNetId, { previewImage: sourceImageUrl }, { skipTracking: true });
    stopLoading(`${unitId}-controlnet-${controlNetId}-processing`);
    return;
  }

  const { startLoading, stopLoading } = getLoadings();

  startLoading(`${unitId}-controlnet-${controlNetId}-processing`);

  try {
    const sourceImagePath = await copyOSSImage(sourceImageUrl, true, '3')
      .then((res) => res.url)
      .then(getImageOssPath);

    await fetchControlNetImage(sourceImagePath, baseType, controlNet, unitId).then((previewImage = null) => {
      if (previewImage) {
        updateControlNet(unitId, controlNetId, { previewImage }, { skipTracking: true });
        stopLoading(`${unitId}-controlnet-${controlNetId}-processing`);
      }
    });
  } catch (error) {
    console.error(error);
    notifyError(translate('controlnet_fail_toast_title'), translate('controlnet_fail_toast_content'));
    stopLoading(`${unitId}-controlnet-${controlNetId}-processing`);
  }
};

export const getControlNetPreviewCacheId = (sourceImagePath, baseType, params) => {
  return (
    'controlnet-preview-' +
    getImageOssKey(sourceImagePath) +
    '-' +
    baseType +
    '-' +
    params.type +
    '-' +
    JSON.stringify(params)
  );
};

const getImagePathKey = (pathOrUrl) => {
  return pathOrUrl ? pathOrUrl.split('/').pop() : null;
};

export const fetchControlNetImage = async (sourceImagePath, baseType, controlNet, unitId) => {
  const { id, controlWeight, startingControlStep, endingControlStep, controlMode, resizeMode } = controlNet;

  try {
    const model = controlNet.model || controlNetConfig[controlNet.type]?.[baseType]?.models[0];
    const preprocessor = controlNet.preprocessor || controlNetConfig[controlNet.type]?.[baseType]?.preprocessors[0];

    const annotationParameters = processorSubParams[preprocessor]
      ? {
          [processorSubParams[preprocessor].name]: Object.fromEntries(
            processorSubParams[preprocessor].items.map((key) => {
              return [key, controlNet[key] || controlNetInitialValues[key]];
            }),
          ),
        }
      : {};

    const params = {
      unitOrder: 1,
      enable: 1,
      controlWeight: Number(controlWeight),
      startingControlStep,
      endingControlStep,
      pixelPerfect: 0,
      pixelErfect: 0,
      controlMode: Number(controlMode),
      resizeMode: Number(resizeMode),
      allowPreview: 1,
      preprocessor: preprocessor,
      model: model,
      annotationParameters: annotationParameters,
      sourceImageId: sourceImagePath,
    };

    const cacheId = getControlNetPreviewCacheId(sourceImagePath, baseType, params);

    if (getSharedData(cacheId)) {
      return getSharedData(cacheId);
    }

    const imageUrl = await requestShakeImage(
      {
        mode: 1,
        generateType: 5,
        controlNet: [params],
      },
      {
        onError: (error) => {
          throw new Error(error.message || error);
        },
      },
    )
      .then((res) => res.images[0].previewPath)
      .then(stripToken);

    const isSimpleMode = getTempState('globalMode') === 'simple';
    mc(isSimpleMode ? 'tool.simple.reference.success' : 'tool.pro.reference.success');

    try {
      const result = compressWithSuffix(imageUrl);
      setSharedData(cacheId, result);

      const { controlnets = [] } = getUnitData(unitId);
      const latestControlNet = controlnets.find((item) => item.id === id);

      const lastestImageKey = getImagePathKey(latestControlNet?.sourceImage?.url);
      const currentImageKey = getImagePathKey(sourceImagePath);

      if (latestControlNet?.type !== controlNet.type || lastestImageKey !== currentImageKey) {
        // 如果这个controlNet的type或者原图已经被修改了，就不更新previewImage
        return null;
      }

      return result;
    } catch (error) {
      throw new Error(translate('controlnet_preprocess_network_error'));
    }
  } catch (error) {
    console.error(error);
    mc('tool.pro.reference.fail');
    notifyError(translate('controlnet_fail_toast_title'), translate('controlnet_fail_toast_content'));
    return null;
  }
};

export const updatePromptFromImage = async (image, unitId, isReplace) => {
  if (!image) return '';

  const { prompt = '' } = getUnitData(unitId);
  const _prompt = await fetchPrompt(image);

  mc('tool.canvas.copy_prompt', { prompt });

  if (_prompt) {
    // navigator.clipboard.writeText(_prompt);
    const value = isReplace ? _prompt : prompt + _prompt;
    updateUnitData(unitId, { prompt: value });
    const start = prompt?.length || 0;
    const end = start + _prompt.length;
    setTimeout(() => {
      let promptDom = document.getElementById(`prompt-input-${unitId}`);

      if (!promptDom) return;

      if (promptDom.tagName !== 'TEXTAREA') {
        promptDom = promptDom.querySelector('textarea');
      }

      if (promptDom) {
        promptDom.selectionStart = start;
        promptDom.selectionEnd = end;
        promptDom.focus();

        setTimeout(() => {
          if (promptDom) {
            promptDom.selectionStart = 0;
            promptDom.selectionEnd = 0;
            promptDom.focus();
          }
        }, 2000);
      }
    }, 500);
    // notifySuccess(translate('prompt_copy_toast'));
  }
};

// updateUnitData
export const applyColorReferenceFromImg = (element, unitId) => {
  if (!element) return;
  unitId = unitId || getActiveGeneratorNodeId();
  updateUnitData(unitId, { colorReferenceImg: element?.originalUrl || element?.url });
  setTimeout(() => {
    setTempState({ exclusiveWindow: `colorReference-options-${unitId}` });
  }, 50);
};
